import { inject, Injectable, NgZone } from '@angular/core';
import { BrowserStorage } from '@common/enums';
import { AuthUser } from '@common/models';
import { LocalStorageService } from '@common/services';
import { ConfigurationService } from '@common/services/configuration.service';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly config = inject(ConfigurationService);
  private readonly ngZone = inject(NgZone);
  private readonly storageService = inject(LocalStorageService);

  public initialize(user: AuthUser) {
    if (this.needDeclinePostHog() || !this.storageService.get<string>(BrowserStorage.Token)) return;

    this.ngZone.runOutsideAngular(async () => {
      const { default: posthog } = await import('posthog-js');

      posthog.init(environment.posthog.prod.key, {
        disable_session_recording: true,
        api_host: environment.posthog.prod.serverUrl,
        loaded: () => {
          const { Email, DomainId, DomainName, Id, Name, Provider, RestoreEnable, SignInEnable } = user;

          posthog.identify(user.Id, { Email, DomainId, DomainName, Id, Name, Provider, RestoreEnable, SignInEnable });
        }
      });
    });
  }

  public needDeclinePostHog(): boolean {
    return !this.isAcceptedHost(location.host);
  }

  public isAcceptedHost(host: string): boolean {
    const { acceptedHosts, declinedHosts } = this.config.get('posthog') || {};

    if (declinedHosts?.some((mask) => this.isMatchWithMask(host, mask))) {
      return false;
    }

    if (acceptedHosts?.some((mask) => this.isMatchWithMask(host, mask))) {
      return true;
    }

    return false;
  }

  private isMatchWithMask(value: string, mask: string): boolean {
    const lValue = value.toLowerCase();
    const lMask = mask.toLowerCase();

    if (lMask.startsWith('*')) {
      return lValue.endsWith(lMask.slice(1));
    }

    if (lMask.endsWith('*')) {
      return lValue.startsWith(lMask.slice(0, -1));
    }

    return lValue === lMask;
  }
}
